/**
 * Gen-3 cartridge natures — the 25-name union + per-nature stat modifier
 * table. Source of truth for the offer-time nature roll (state/draftRoll.ts)
 * and the headline curator's "Adamant +Atk / -SpA" line (Step 5's
 * data/draftHeadline.ts).
 *
 * Five natures are neutral (no boost, no penalty): hardy, docile, serious,
 * bashful, quirky. The other 20 form a 5×5 grid — five stats can boost,
 * five can take the penalty, minus the diagonal that loops back to neutral.
 * Stat keys match the rest of the codebase's 'atk/def/spa/spd/spe' spelling
 * (no HP — natures never touch HP).
 */

export type Nature =
  | 'hardy'
  | 'lonely'
  | 'brave'
  | 'adamant'
  | 'naughty'
  | 'bold'
  | 'docile'
  | 'relaxed'
  | 'impish'
  | 'lax'
  | 'timid'
  | 'hasty'
  | 'serious'
  | 'jolly'
  | 'naive'
  | 'modest'
  | 'mild'
  | 'quiet'
  | 'bashful'
  | 'rash'
  | 'calm'
  | 'gentle'
  | 'sassy'
  | 'careful'
  | 'quirky';

/** Cartridge-canonical ordering. Used by rollNature for uniform indexing.
 *  Don't reorder — Section 6 of the Pokédex design doc freezes the byte
 *  stability of draft offers against this index sequence. */
export const NATURES: readonly Nature[] = [
  'hardy',
  'lonely',
  'brave',
  'adamant',
  'naughty',
  'bold',
  'docile',
  'relaxed',
  'impish',
  'lax',
  'timid',
  'hasty',
  'serious',
  'jolly',
  'naive',
  'modest',
  'mild',
  'quiet',
  'bashful',
  'rash',
  'calm',
  'gentle',
  'sassy',
  'careful',
  'quirky',
] as const;

export type StatKey = 'atk' | 'def' | 'spa' | 'spd' | 'spe';

export interface NatureModifier {
  /** Stat that gets the ×1.1 boost. null for neutral natures. */
  plus: StatKey | null;
  /** Stat that gets the ×0.9 penalty. null for neutral natures. */
  minus: StatKey | null;
}

/** Per-nature stat modifiers, cartridge canon. Five neutral (no boost, no
 *  penalty), twenty asymmetric (one boost, one penalty). Used by the
 *  headline curator to surface "Adamant +Atk / -SpA"-style traits and (in
 *  v0.2+ if EVs land) by sim's stat math. */
export const NATURE_MODS: Record<Nature, NatureModifier> = {
  hardy: { plus: null, minus: null },
  lonely: { plus: 'atk', minus: 'def' },
  brave: { plus: 'atk', minus: 'spe' },
  adamant: { plus: 'atk', minus: 'spa' },
  naughty: { plus: 'atk', minus: 'spd' },
  bold: { plus: 'def', minus: 'atk' },
  docile: { plus: null, minus: null },
  relaxed: { plus: 'def', minus: 'spe' },
  impish: { plus: 'def', minus: 'spa' },
  lax: { plus: 'def', minus: 'spd' },
  timid: { plus: 'spe', minus: 'atk' },
  hasty: { plus: 'spe', minus: 'def' },
  serious: { plus: null, minus: null },
  jolly: { plus: 'spe', minus: 'spa' },
  naive: { plus: 'spe', minus: 'spd' },
  modest: { plus: 'spa', minus: 'atk' },
  mild: { plus: 'spa', minus: 'def' },
  quiet: { plus: 'spa', minus: 'spe' },
  bashful: { plus: null, minus: null },
  rash: { plus: 'spa', minus: 'spd' },
  calm: { plus: 'spd', minus: 'atk' },
  gentle: { plus: 'spd', minus: 'def' },
  sassy: { plus: 'spd', minus: 'spe' },
  careful: { plus: 'spd', minus: 'spa' },
  quirky: { plus: null, minus: null },
};

/** Convenience predicate — five natures don't shift stats at all. */
export function isNeutralNature(n: Nature): boolean {
  return NATURE_MODS[n].plus === null;
}
